import type { CreditAllowance } from '../../contracts';
import {
  getCreditAllowanceUsagePercent,
  isComplimentaryCreditAllowance,
} from './credit-allowance.utils';

/** The countdown surfaces appear once this many days or fewer are left. */
export const COMPLIMENTARY_TRIAL_END_COUNTDOWN_DAYS = 7;

/** Share of the plan's own limit (`display_limit`) at which a member counts as active. */
export const COMPLIMENTARY_TRIAL_END_ACTIVE_USAGE_PERCENT = 50;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type ComplimentaryTrialEndCohort = 'active' | 'inactive';

export type ComplimentaryTrialEndState = {
  /** Whole days left, rounded up, so the last day reads 1 rather than 0. */
  daysLeft: number;
  cycleEnd: string;
  usagePercent: number;
  cohort: ComplimentaryTrialEndCohort;
};

/**
 * Days until the complimentary cycle ends, rounded up. `null` once it has ended
 * or when `cycle_end` is unparseable.
 */
export function getComplimentaryDaysLeft(
  allowance: Pick<CreditAllowance, 'cycle_end'>,
  now: Date
): number | null {
  const remainingMs = new Date(allowance.cycle_end).getTime() - now.getTime();
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
    return null;
  }
  return Math.ceil(remainingMs / MS_PER_DAY);
}

/**
 * The last-week state for a member spending against the uncapped opt-in cycle.
 * `cycle_end` is that cycle's own end, so an extended complimentary period moves
 * the countdown with it. Once the cycle lapses the API returns the capped cadence
 * cycle and this returns `null` without any extra state.
 */
export function deriveComplimentaryTrialEndState({
  allowance,
  isCohortEnabled,
  isTrialEndEnabled,
  isExcluded,
  now,
}: {
  allowance: CreditAllowance | null | undefined;
  /** Opt-in rollout flag AND `heidi_version === 2`. */
  isCohortEnabled: boolean;
  isTrialEndEnabled: boolean;
  /** Members with their own countdown (14-day trial) or already on a Max seat. */
  isExcluded: boolean;
  now: Date;
}): ComplimentaryTrialEndState | null {
  if (!isTrialEndEnabled || !isCohortEnabled || isExcluded) {
    return null;
  }
  if (allowance == null || !isComplimentaryCreditAllowance(allowance)) {
    return null;
  }
  const daysLeft = getComplimentaryDaysLeft(allowance, now);
  if (daysLeft == null || daysLeft > COMPLIMENTARY_TRIAL_END_COUNTDOWN_DAYS) {
    return null;
  }
  const usagePercent = getCreditAllowanceUsagePercent(allowance);
  return {
    daysLeft,
    cycleEnd: allowance.cycle_end,
    usagePercent,
    cohort:
      usagePercent >= COMPLIMENTARY_TRIAL_END_ACTIVE_USAGE_PERCENT
        ? 'active'
        : 'inactive',
  };
}
